import React, { useEffect, useMemo, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { Button, Layout, Typography, message, Modal, Result, Spin } from 'antd';
import { Content } from 'antd/es/layout/layout';

const { Title, Text, Paragraph } = Typography;

/**
 * Pagina puente para autenticar la EXTENSION de Eclipse Theia con Google.
 *
 * Flujo (device/loopback):
 *   1. La extension abre esta pagina con ?redirect=<callback local>&state=<random>.
 *   2. El alumno inicia sesion con Google (mismo flujo que el Login normal).
 *   3. Obtenido el token de Lanterns, redirigimos a
 *      <redirect>?token=<token>&state=<state> para que la extension lo capture.
 *
 * Seguridad:
 *   - Solo se permite redirigir a 127.0.0.1 / localhost (callback local de la
 *     extension). Cualquier otro host se rechaza (evita robo de token).
 *   - El parametro state se devuelve tal cual para que la extension valide CSRF.
 */

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID ||
    "263998953995-lj8b0d0q5boof4j70jrtlch4rvjtulqi.apps.googleusercontent.com";

function isLocalRedirect(redirect: string): boolean {
    try {
        const u = new URL(redirect);
        const isHttp = u.protocol === 'http:' || u.protocol === 'https:';
        const isLoopback = u.hostname === '127.0.0.1' || u.hostname === 'localhost' || u.hostname === '[::1]';
        return isHttp && isLoopback;
    } catch {
        return false;
    }
}

const IdeAuth = () => {
    const params = useMemo(() => new URLSearchParams(window.location.search), []);
    const redirect = params.get('redirect') || '';
    const state = params.get('state') || '';

    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Modal de confirmacion para crear usuario nuevo de Google
    const [googleModalOpen, setGoogleModalOpen] = useState(false);
    const [pendingGoogle, setPendingGoogle] = useState<{ token: string; email: string; nom: string; cognoms: string } | null>(null);

    const validRedirect = isLocalRedirect(redirect);

    useEffect(() => {
        document.title = 'Lanterns · Autenticar IDE';
    }, []);

    const finish = (token: string) => {
        // Redirige al callback local de la extension con el token y el state.
        const sep = redirect.includes('?') ? '&' : '?';
        const url = `${redirect}${sep}token=${encodeURIComponent(token)}&state=${encodeURIComponent(state)}`;
        setDone(true);
        // Pequeño retardo para que se vea el mensaje antes de saltar.
        setTimeout(() => { window.location.href = url; }, 600);
    };

    const handleGoogleLogin = async (credentialResponse: any) => {
        setLoading(true);
        setError(null);
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const res = await fetch(`${apiUrl}:8061/login/google`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token: credentialResponse.credential,
                    clientId: GOOGLE_CLIENT_ID
                })
            });
            if (res.ok) {
                const usuariP = await res.json();
                if (usuariP && usuariP.exists === false) {
                    setPendingGoogle({
                        token: credentialResponse.credential,
                        email: usuariP.email,
                        nom: usuariP.nom,
                        cognoms: usuariP.cognoms
                    });
                    setGoogleModalOpen(true);
                    return;
                }
                if (usuariP && usuariP.token) {
                    finish(usuariP.token);
                } else {
                    setError('Respuesta de login sin token.');
                }
            } else {
                const text = await res.text();
                setError(text || 'Error al iniciar sesión con Google');
            }
        } catch (e) {
            setError('Error de conexión con el backend.');
        } finally {
            setLoading(false);
        }
    };

    const confirmGoogleCreate = async () => {
        if (!pendingGoogle) return;
        setLoading(true);
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const res = await fetch(`${apiUrl}:8061/login/google/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token: pendingGoogle.token,
                    clientId: GOOGLE_CLIENT_ID
                })
            });
            if (res.ok) {
                const usuariP = await res.json();
                setGoogleModalOpen(false);
                setPendingGoogle(null);
                if (usuariP && usuariP.token) {
                    finish(usuariP.token);
                } else {
                    setError('Respuesta de creación sin token.');
                }
            } else {
                const text = await res.text();
                message.error(text || 'Error al crear el usuario con Google');
            }
        } catch (e) {
            message.error('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const cancelGoogleCreate = () => {
        setGoogleModalOpen(false);
        setPendingGoogle(null);
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Content style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                <div style={{
                    width: 420, maxWidth: '100%', background: '#fff', padding: 32,
                    borderRadius: 12, boxShadow: '0 4px 30px rgba(0,0,0,0.1)', textAlign: 'center'
                }}>
                    <Title level={3}>Conectar el IDE con Lanterns</Title>

                    {!validRedirect ? (
                        <Result
                            status="error"
                            title="Enlace no válido"
                            subTitle="Abre esta página desde el comando 'Iniciar sesión con Google' de la extensión del IDE."
                        />
                    ) : done ? (
                        <Result
                            status="success"
                            title="¡Listo!"
                            subTitle="Volviendo al IDE. Ya puedes cerrar esta pestaña."
                        />
                    ) : (
                        <>
                            <Paragraph type="secondary">
                                Inicia sesión con Google para autorizar la telemetría de tu IDE.
                                El token se enviará solo a tu ordenador (127.0.0.1).
                            </Paragraph>

                            {loading ? (
                                <Spin style={{ marginTop: 24 }} />
                            ) : (
                                <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center' }}>
                                    <GoogleLogin
                                        onSuccess={handleGoogleLogin}
                                        onError={() => setError('Error al iniciar sesión con Google')}
                                        theme="outline"
                                        size="large"
                                        width={260}
                                        text="continue_with"
                                    />
                                </div>
                            )}

                            {error && (
                                <Text type="danger" style={{ display: 'block', marginTop: 16 }}>{error}</Text>
                            )}
                        </>
                    )}
                </div>
            </Content>

            <Modal
                open={googleModalOpen}
                title="Usuario no encontrado"
                okText="Crear cuenta"
                cancelText="Cancelar"
                confirmLoading={loading}
                onOk={confirmGoogleCreate}
                onCancel={cancelGoogleCreate}
            >
                <p>No hemos encontrado ninguna cuenta asociada a este correo de Google. ¿Quieres crear un usuario nuevo con estos datos?</p>
                {pendingGoogle && (
                    <div style={{ marginTop: 12, lineHeight: 1.8 }}>
                        <div><b>Nombre:</b> {`${pendingGoogle.nom} ${pendingGoogle.cognoms}`.trim() || '—'}</div>
                        <div><b>Correo:</b> {pendingGoogle.email}</div>
                    </div>
                )}
            </Modal>
        </Layout>
    );
};

export default IdeAuth;
